import { MessagingSession } from '@/components/MessagingSession';
import { Outlet, useLocation } from 'react-router-dom';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@gokrew/ui/hooks';
// import { AssistantLauncher } from "@/components/assistant";
import { PageHeaderProvider } from '@/context/PageHeaderContext';
import { AssetTourProvider } from '@/context/AssetTourContext';
import { useAuth } from '@/context/AuthContext';
import { TourProvider, useDialog } from '@gokrew/ui';
import { PageSuspenseFallback } from '@gokrew/ui/components/PageSuspenseFallback';
import { InitialResetPasswordPage } from '@/pages/auth/InitialResetPasswordPage';
import { AssistantLauncher, AssistantProvider } from '@/components/assistant';
import Header from '@/components/Header';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { BillingPaywallListener } from '@/components/billing/BillingPaywallListener';
import { Sidebar } from '@/components/Sidebar/Sidebar';
import { preloadRoutes } from '@/utility/routePreloader';
import { usePermissions } from '@/context/PermissionContext';
import { CommandPaletteProvider } from '@/context/CommandPaletteContext';
import { CommandPalette } from '@/components/CommandPalette';

export function MainLayout() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const matches = useMediaQuery('(min-width: 1024px)');
  const { permissions } = usePermissions();
  const hasOpenedResetDialog = useRef(false);
  const { pathname } = useLocation();
  // Recruiting is a wide workspace (pipeline board, candidate profile), so on
  // a desktop the sidebar folds to its icon rail every time Recruiting opens
  // (JP, 2026-10-28). Expanding it there lasts until Recruiting is left;
  // leaving restores the full sidebar.
  const inRecruiting = pathname === '/ats' || pathname.startsWith('/ats/');
  useEffect(() => {
    setIsSidebarCollapsed(!matches || inRecruiting);
  }, [matches, inRecruiting]);

  // Reading `prev` from the functional updater (rather than the stale
  // `isCollapsed` prop closed over by a rapid double-click) means a repeat
  // call for the same target is already a no-op here — so a second class
  // toggle never restarts the CSS width transition mid-flight. Don't also
  // block calls for the *other* target (e.g. Header's explicit collapse-open
  // call) while a transition is in flight — that would silently drop a
  // genuine user action.
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed((prev) => (prev === collapsed ? prev : collapsed));
  }, []);

  const { openDialog } = useDialog();
  const { user, isMockLogin } = useAuth();

  useEffect(() => {
    if (permissions.size > 0) {
      preloadRoutes(permissions);
    }
  }, [permissions]);

  useEffect(() => {
    if (
      !isMockLogin &&
      user?.temporaryPasswordGeneratedAt !== null &&
      user?.passwordChangedAt === null &&
      !hasOpenedResetDialog.current
    ) {
      hasOpenedResetDialog.current = true;

      openDialog({
        title: 'Reset Password',
        hideHeader: true,
        containerClass: 'w-[480px] max-w-[480px]',
        disableClose: true,
        content: <InitialResetPasswordPage dialogMode />,
      });
    }
  }, [user, openDialog, isMockLogin]);

  return (
    <MessagingSession>
      <TourProvider>
        <AssetTourProvider>
          <PageHeaderProvider>
            <CommandPaletteProvider>
              <AssistantProvider>
                <div className="flex h-screen">
                  <Sidebar
                    isCollapsed={isSidebarCollapsed}
                    onCollapsedChange={setSidebarCollapsed}
                  />
                  <div className="flex flex-col w-full overflow-x-auto md:ml-16 lg:ml-0">
                    {isMockLogin && <ImpersonationBanner />}
                    <Header onMenuClick={() => setSidebarCollapsed(false)} />
                    <main className="flex-1 overflow-auto flex flex-col mt-4">
                      <div className="px-2 pb-3 md:px-3 md:pb-5 xl:px-4 xl:pb-8 flex-1 flex flex-col has-[[data-fill-viewport]]:min-h-0 has-[[data-fill-viewport]]:pb-3!">
                        {/* A page whose code is still loading shows here, not
                          as the full-page loader that would hide the shell. */}
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <Outlet />
                        </Suspense>

                        <AssistantLauncher />
                        <BillingPaywallListener />
                      </div>
                    </main>
                  </div>
                </div>
              </AssistantProvider>
              <CommandPalette />
            </CommandPaletteProvider>
          </PageHeaderProvider>
        </AssetTourProvider>
      </TourProvider>
    </MessagingSession>
  );
}
